import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Layers, Pencil, Plus, Trash2, Upload, Wallet, X } from "lucide-react";
import { api, apiError, mediaUrl } from "@/lib/api";
import { fmtDate, fmtGNF, PAYMENT_STATUS_STYLES, STATUS_STYLES } from "@/lib/format";
import { ABtn, AInput, ALabel, ASelect, Panel } from "@/pages/admin/AdminLayout";

const Stat = ({ label, value, hint, tone = "gold" }) => (
  <div className="rounded-2xl border border-slate-800 bg-slate-900 p-4" data-testid={`vendor-stat-${label}`}>
    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
    <p className={`font-num mt-2 text-xl font-bold ${tone === "gold" ? "text-gold" : "text-emerald-400"}`}>{value}</p>
    {hint && <p className="mt-1 text-[11px] text-slate-500">{hint}</p>}
  </div>
);

export function VendorDashboard() {
  const [w, setW] = useState(null);

  useEffect(() => {
    api.get("/vendor/wallet").then((r) => setW(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);

  if (!w) return <p className="text-sm text-slate-400">Chargement…</p>;

  return (
    <div className="space-y-4" data-testid="vendor-dashboard">
      <h1 className="font-display text-2xl font-bold text-white">Tableau de bord vendeur</h1>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Total des ventes" value={fmtGNF(w.total_sales)} tone="emerald" />
        <Stat label="Commissions EMPIRE-SHOP" value={fmtGNF(w.total_commission)} />
        <Stat label="Montant en attente" value={fmtGNF(w.pending_amount)} hint="Commandes non encore livrées" />
        <Stat label="Solde disponible" value={fmtGNF(w.available_amount)} tone="emerald" hint={`Déjà payé : ${fmtGNF(w.paid_amount)}`} />
        <Stat label="Mes produits" value={w.products_total} hint={`${w.products_approved} validé(s)`} />
        <Stat label="Stock faible" value={w.low_stock} />
      </div>

      <Panel title="Dernières ventes" subtitle="Produit, montant, commission et part vendeur" testid="vendor-recent-sales">
        <div className="space-y-2">
          {w.history.length === 0 && <p className="text-sm text-slate-500">Aucune vente pour le moment.</p>}
          {w.history.slice(0, 10).map((h, i) => (
            <div key={i} className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-800 p-3 text-sm">
              <span className="font-num text-slate-400">{h.order_number}</span>
              <span className="min-w-0 flex-1 truncate text-white">{h.product_name}</span>
              <span className={`ds-chip ${STATUS_STYLES[h.status] || ""}`}>{h.status}</span>
              <span className="font-num text-slate-300">{h.quantity} × · {fmtGNF(h.total)}</span>
              <span className="font-num text-rose-300">- {fmtGNF(h.commission_amount)}</span>
              <span className="font-num font-bold text-emerald-400">{fmtGNF(h.vendor_amount)}</span>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}

const EMPTY = {
  name: "",
  sku: "",
  description: "",
  category_id: "",
  images: [],
  price: 0,
  wholesale_price: "",
  wholesale_min_qty: "",
  compare_price: "",
  stock: 0,
  low_stock_threshold: 5,
  is_active: true,
};

export function VendorProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [p, c] = await Promise.all([api.get("/vendor/products"), api.get("/categories")]);
      setProducts(p.data);
      setCategories(c.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setBusy(true);
    const payload = {
      name: form.name,
      sku: form.sku,
      description: form.description,
      category_id: form.category_id,
      images: form.images,
      price: Number(form.price) || 0,
      stock: Number(form.stock) || 0,
      low_stock_threshold: Number(form.low_stock_threshold) || 0,
      wholesale_price: form.wholesale_price === "" ? null : Number(form.wholesale_price),
      wholesale_min_qty: form.wholesale_min_qty === "" ? null : Number(form.wholesale_min_qty),
      compare_price: form.compare_price === "" ? null : Number(form.compare_price),
      is_active: form.is_active,
    };
    try {
      if (form.id) await api.put(`/vendor/products/${form.id}`, payload);
      else await api.post("/vendor/products", payload);
      toast.success(form.id ? "Produit modifié — en attente de validation" : "Produit créé — en attente de validation");
      setForm(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer « ${p.name} » ?`)) return;
    try {
      await api.delete(`/vendor/products/${p.id}`);
      toast.success("Produit supprimé");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, images: [...f.images, data.url] }));
      toast.success("Image téléversée");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="vendor-products-page">
      <Panel
        title="Mes produits"
        subtitle={`${products.length} produit(s) · validation administrateur requise avant publication`}
        actions={
          <ABtn variant="gold" onClick={() => { setForm({ ...EMPTY, category_id: categories[0]?.id || "" }); setImageUrl(""); }} data-testid="vendor-new-product-button">
            <Plus className="h-4 w-4" /> Nouveau produit
          </ABtn>
        }
        testid="vendor-products-panel"
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-left text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-slate-400">
              <tr className="border-b border-slate-800">
                <th className="py-2">Produit</th>
                <th className="py-2">SKU</th>
                <th className="py-2">Catégorie</th>
                <th className="py-2 text-right">Prix détail</th>
                <th className="py-2 text-right">Prix de gros</th>
                <th className="py-2 text-right">Stock</th>
                <th className="py-2">Publication</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-b border-slate-800/60" data-testid={`vendor-product-row-${p.sku}`}>
                  <td className="py-2">
                    <div className="flex items-center gap-2">
                      <div className="h-9 w-9 shrink-0 overflow-hidden rounded-lg bg-slate-800">
                        {(p.images || [])[0] && <img src={mediaUrl(p.images[0])} alt="" className="h-full w-full object-cover" />}
                      </div>
                      <span className="line-clamp-1 text-white">{p.name}</span>
                    </div>
                  </td>
                  <td className="font-num py-2 text-slate-300">{p.sku}</td>
                  <td className="py-2 text-slate-300">{p.category_name}</td>
                  <td className="font-num py-2 text-right text-emerald-400">{fmtGNF(p.price)}</td>
                  <td className="font-num py-2 text-right text-gold">
                    {p.wholesale_price ? `${fmtGNF(p.wholesale_price)} (≥${p.wholesale_min_qty})` : "—"}
                  </td>
                  <td className={`font-num py-2 text-right ${p.stock <= p.low_stock_threshold ? "text-rose-400" : "text-slate-200"}`}>
                    {p.stock <= p.low_stock_threshold && <AlertTriangle className="mr-1 inline h-3 w-3" />}
                    {p.stock}
                  </td>
                  <td className="py-2">
                    <span className={`ds-chip ${p.is_approved ? "border-emerald-700 bg-emerald-950 text-emerald-300" : "border-amber-700 bg-amber-950 text-amber-300"}`}>
                      {p.is_approved ? "Publié" : "À valider"}
                    </span>
                  </td>
                  <td className="py-2">
                    <div className="flex justify-end gap-1">
                      <ABtn variant="ghost" onClick={() => setForm({ ...EMPTY, ...p, wholesale_price: p.wholesale_price ?? "", wholesale_min_qty: p.wholesale_min_qty ?? "", compare_price: p.compare_price ?? "" })} data-testid={`vendor-edit-product-${p.sku}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </ABtn>
                      <ABtn variant="danger" onClick={() => remove(p)} data-testid={`vendor-delete-product-${p.sku}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </ABtn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      {form && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4" data-testid="vendor-product-modal">
          <div className="w-full max-w-2xl rounded-2xl border border-slate-800 bg-slate-900 p-5">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-bold text-white">{form.id ? "Modifier le produit" : "Nouveau produit"}</h3>
              <ABtn variant="ghost" onClick={() => setForm(null)}><X className="h-4 w-4" /></ABtn>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <ALabel>Nom *</ALabel>
                <AInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="vendor-product-name" />
              </div>
              <div>
                <ALabel>SKU / Référence *</ALabel>
                <AInput value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="vendor-product-sku" />
              </div>
              <div>
                <ALabel>Catégorie *</ALabel>
                <ASelect value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} data-testid="vendor-product-category">
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </ASelect>
              </div>
              <div className="sm:col-span-2">
                <ALabel>Description</ALabel>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm text-slate-100 outline-none focus:border-gold"
                  data-testid="vendor-product-description"
                />
              </div>
              <div>
                <ALabel>Prix de vente à l'unité (GNF) *</ALabel>
                <AInput type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="vendor-product-price" />
              </div>
              <div>
                <ALabel>Prix de gros (GNF)</ALabel>
                <AInput type="number" value={form.wholesale_price} onChange={(e) => setForm({ ...form, wholesale_price: e.target.value })} data-testid="vendor-product-wholesale" />
              </div>
              <div>
                <ALabel>Quantité minimale de gros</ALabel>
                <AInput type="number" value={form.wholesale_min_qty} onChange={(e) => setForm({ ...form, wholesale_min_qty: e.target.value })} data-testid="vendor-product-wholesale-qty" />
              </div>
              <div>
                <ALabel>Ancien prix (promo)</ALabel>
                <AInput type="number" value={form.compare_price} onChange={(e) => setForm({ ...form, compare_price: e.target.value })} data-testid="vendor-product-compare" />
              </div>
              <div>
                <ALabel>Quantité disponible</ALabel>
                <AInput type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="vendor-product-stock" />
              </div>
              <div>
                <ALabel>Seuil d'alerte</ALabel>
                <AInput type="number" value={form.low_stock_threshold} onChange={(e) => setForm({ ...form, low_stock_threshold: e.target.value })} data-testid="vendor-product-threshold" />
              </div>

              <div className="sm:col-span-2">
                <ALabel>Photos</ALabel>
                <div className="mt-1 flex flex-wrap gap-2">
                  {form.images.map((img, i) => (
                    <div key={i} className="relative h-20 w-20 overflow-hidden rounded-xl border border-slate-700">
                      <img src={mediaUrl(img)} alt="" className="h-full w-full object-cover" />
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, images: form.images.filter((_, j) => j !== i) })}
                        className="absolute right-1 top-1 grid h-5 w-5 place-items-center rounded-full bg-rose-600 text-white"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  <label className="grid h-20 w-20 cursor-pointer place-items-center rounded-xl border border-dashed border-slate-600 text-slate-400 hover:bg-slate-800">
                    <Upload className="h-5 w-5" />
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])} data-testid="vendor-product-upload" />
                  </label>
                </div>
                <div className="mt-2 flex gap-2">
                  <AInput placeholder="…ou coller une URL d'image" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} data-testid="vendor-product-image-url" />
                  <ABtn
                    variant="ghost"
                    onClick={() => {
                      if (!imageUrl.trim()) return;
                      setForm({ ...form, images: [...form.images, imageUrl.trim()] });
                      setImageUrl("");
                    }}
                    data-testid="vendor-product-add-image-url"
                  >
                    Ajouter
                  </ABtn>
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-slate-300 sm:col-span-2">
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="vendor-product-active" />
                Produit actif
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <ABtn variant="ghost" onClick={() => setForm(null)}>Annuler</ABtn>
              <ABtn variant="gold" onClick={save} disabled={busy} data-testid="vendor-product-save">Enregistrer</ABtn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function VendorOrders() {
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    api.get("/vendor/orders").then((r) => setOrders(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);

  return (
    <Panel title="Mes commandes" subtitle={`${orders.length} commande(s) contenant mes produits`} testid="vendor-orders-page">
      <div className="space-y-2">
        {orders.length === 0 && <p className="text-sm text-slate-500">Aucune commande pour le moment.</p>}
        {orders.map((o) => (
          <div key={o.id} className="rounded-xl border border-slate-800 p-3" data-testid={`vendor-order-row-${o.number}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="font-num text-sm font-bold text-white">{o.number}</p>
                <p className="text-[11px] text-slate-400">{o.customer_name} · {o.zone_name} · {fmtDate(o.created_at)}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`ds-chip ${STATUS_STYLES[o.status] || ""}`}>{o.status}</span>
                <span className={`ds-chip ${PAYMENT_STATUS_STYLES[o.payment_status] || ""}`}>{o.payment_status}</span>
              </div>
            </div>
            <div className="mt-2 space-y-1">
              {o.items.map((it) => (
                <div key={it.id} className="flex flex-wrap items-center gap-3 text-sm">
                  <span className="min-w-0 flex-1 truncate text-slate-200">{it.product_name}</span>
                  {it.is_wholesale && <Layers className="h-3.5 w-3.5 text-gold" />}
                  <span className="font-num text-slate-400">{it.quantity} × {fmtGNF(it.unit_price)}</span>
                  <span className="font-num text-white">{fmtGNF(it.total)}</span>
                </div>
              ))}
            </div>
            <div className="font-num mt-2 flex flex-wrap justify-end gap-4 border-t border-slate-800 pt-2 text-xs">
              <span className="text-slate-400">Vente {fmtGNF(o.items_total)}</span>
              <span className="text-rose-300">Commission {fmtGNF(o.commission_total)}</span>
              <span className="font-bold text-emerald-400">Ma part {fmtGNF(o.vendor_total)}</span>
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

export function VendorFinances() {
  const [w, setW] = useState(null);

  useEffect(() => {
    api.get("/vendor/wallet").then((r) => setW(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);

  if (!w) return <p className="text-sm text-slate-400">Chargement…</p>;

  return (
    <div className="space-y-4" data-testid="vendor-finances-page">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Stat label="Ventes" value={fmtGNF(w.total_sales)} tone="emerald" />
        <Stat label="Commissions" value={fmtGNF(w.total_commission)} />
        <Stat label="En attente" value={fmtGNF(w.pending_amount)} />
        <Stat label="Disponible" value={fmtGNF(w.available_amount)} tone="emerald" />
        <Stat label="Déjà payé" value={fmtGNF(w.paid_amount)} />
      </div>

      <Panel title="Historique financier" subtitle="Chaque ligne de vente avec commission et part vendeur" testid="vendor-finance-history">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-slate-400">
              <tr className="border-b border-slate-800">
                <th className="py-2">Commande</th>
                <th className="py-2">Produit</th>
                <th className="py-2">Statut</th>
                <th className="py-2 text-right">Vente</th>
                <th className="py-2 text-right">Taux</th>
                <th className="py-2 text-right">Commission</th>
                <th className="py-2 text-right">Ma part</th>
              </tr>
            </thead>
            <tbody>
              {w.history.map((h, i) => (
                <tr key={i} className="border-b border-slate-800/60">
                  <td className="font-num py-2 text-slate-300">{h.order_number}</td>
                  <td className="py-2 text-white">{h.product_name}</td>
                  <td className="py-2"><span className={`ds-chip ${STATUS_STYLES[h.status] || ""}`}>{h.status}</span></td>
                  <td className="font-num py-2 text-right text-slate-200">{fmtGNF(h.total)}</td>
                  <td className="font-num py-2 text-right text-slate-400">{h.commission_rate} %</td>
                  <td className="font-num py-2 text-right text-rose-300">{fmtGNF(h.commission_amount)}</td>
                  <td className="font-num py-2 text-right font-semibold text-emerald-400">{fmtGNF(h.vendor_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel title="Paiements reçus" subtitle="Versements enregistrés par EMPIRE-SHOP" testid="vendor-payouts">
        <div className="space-y-2">
          {w.payouts.length === 0 && <p className="text-sm text-slate-500">Aucun versement pour le moment.</p>}
          {w.payouts.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-800 p-3 text-sm">
              <Wallet className="h-4 w-4 text-emerald-400" />
              <span className="font-num flex-1 font-bold text-emerald-400">{fmtGNF(p.amount)}</span>
              <span className="text-slate-400">{p.method}</span>
              <span className="font-num text-[11px] text-slate-500">{p.reference || "—"}</span>
              <span className="text-[11px] text-slate-500">{fmtDate(p.created_at)}</span>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
